import * as React from "react"

import { cn } from "@/lib/utils"
import { looksUrlEncoded, cleanPastedText } from "../../utils/pasteCleaner"

/**
 * Shadcn Textarea, hardened to auto-decode URL-encoded paste content.
 *
 * Why: clinical staff sometimes paste text from EMRs / URLs that has been
 * URL-encoded (e.g. `Patient%20has%20FAST%207c`). Without this guard, the
 * encoded blob lands in our textarea as-is and is unreadable. This wrapper
 * intercepts the paste event, detects URL-encoded content, decodes it, and
 * inserts the cleaned text — preserving cursor position and selection.
 *
 * Plain (non-encoded) pastes fall through to the browser's default paste
 * with NO behaviour change.
 */
const Textarea = React.forwardRef(({ className, onPaste, ...props }, ref) => {
  const handlePaste = (e) => {
    // Run user-supplied onPaste first (in case it calls preventDefault)
    if (typeof onPaste === "function") onPaste(e)
    if (e.defaultPrevented) return

    const raw = e.clipboardData?.getData("text/plain")
    if (!raw || !looksUrlEncoded(raw)) return // let browser paste normally

    e.preventDefault()
    const cleaned = cleanPastedText(raw)

    // If the textarea is controlled (has onChange), synthesize a change event
    // so the parent React state updates. If uncontrolled (ref-only), we
    // mutate the DOM value directly.
    const target = e.target
    const start = typeof target.selectionStart === "number" ? target.selectionStart : (target.value || "").length
    const end = typeof target.selectionEnd === "number" ? target.selectionEnd : (target.value || "").length
    const current = target.value || ""
    const nextValue = current.slice(0, start) + cleaned + current.slice(end)

    // Use the native setter so React picks up the change
    const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set
    if (setter) {
      setter.call(target, nextValue)
      target.dispatchEvent(new Event("input", { bubbles: true }))
    } else {
      target.value = nextValue
    }

    // Restore cursor position after the pasted block
    requestAnimationFrame(() => {
      try {
        target.selectionStart = target.selectionEnd = start + cleaned.length
      } catch {
        /* element may have unmounted */
      }
    })
  }

  return (
    <textarea
      className={cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      onPaste={handlePaste}
      {...props} />
  );
})
Textarea.displayName = "Textarea"

export { Textarea }
